/**
 * Utils for 'the JavaScript language'.
 */

/**
 * Do nothing.
 *
 * An alternative to inline anonymous functions for when the reference must
 * stay the same.
 */
export function noop() {}

/**
 * Create a function that delays invoking until after enough time have elapsed
 * since the last time the function was invoked.
 *
 * Use lodash for a more powerful version with options.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {Function} The debounced function.
 * @example
 *
 * // Only called when resizing hasn't happened for 250 miliseconds
 * this.on(window, 'resize', debounce(this.handleWindowResize, 250));
 */
export function debounce(func, wait) {
  let timeout;
  return function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(function debouncedTimeout() {
      timeout = null;
      func(...args);
    }, wait);
  };
}

/**
 * Create a function that only invokes at most once within the specified window
 * of time.
 *
 * Called once right away, then at most once every X miliseconds and finally
 * once after the invokations have stopped (i.e. the 'trailing' call).
 *
 * Subsequent calls to the throttled function return the result of the last
 * invocation that passed.
 *
 * Use lodash for a more powerful version with options.
 *
 * @param {Function} func - The function to throttle.
 * @param {number} wait - The number of milliseconds to throttle invocations to.
 * @returns {Function} The throttled function.
 * @example
 *
 * // Called at most four times per second while resizing
 * this.on(window, 'resize', throttle(this.handleWindowResize, 250));
 */
export function throttle(func, wait) {
  let isThrottled = false;
  let throttleArgs = null;
  let result;

  function throttled(...args) {
    if (isThrottled) {
      throttleArgs = args;
      return result;
    }

    isThrottled = true;
    result = func(...args);

    setTimeout(function throttleTimeout() {
      isThrottled = false;
      if (throttleArgs) {
        throttled(...throttleArgs);
        throttleArgs = null;
      }
    }, wait);

    return result;
  }

  return throttled;
}

/**
 * Get a random integer between two values (inclusive).
 *
 * @param {number} min - Minimum value.
 * @param {number} max - Maximum value.
 * @returns {number}
 * @example
 *
 * getRandomIntBetween(5, 10);
 * // => 7
 */
export function getRandomIntBetween(min, max) {
  /* eslint-disable no-param-reassign */
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Cycle through values from left to right.
 *
 * @param {*} current - The current value in the cycle.
 * @param  {...*} values - Values to cycle through.
 * @returns {*} The next value, or first if at the end of the cycle.
 * @example
 *
 * const current = 1;
 * cycleValues(current, 1, 2, 3);
 * // => 2
 *
 * const current = 'c';
 * cycleValues(current, 'a', 'b', 'c');
 * // => 'a'
 */
export function cycleValues(current, ...values) {
  const index = values.indexOf(current);
  if (index === -1) {
    throw new Error(`'${current}' doesn't exist in supplied values.`);
  }
  return index + 1 >= values.length ? values[0] : values[index + 1];
}

/**
 * Conditionally assign properties to a new object.
 *
 * @param {Function} condition - Condition that must return true for the
 *   property to be included in the resulting object. Called for each property
 *   with (value, key, object).
 * @param {...object} objects - Objects to assign.
 * @returns {object} The new object.
 */
export function conditionalAssign(condition, ...objects) {
  const filtered = objects.map((obj) =>
    Object.keys(obj).reduce((result, key) => {
      if (!condition(obj[key], key, obj)) {
        return result;
      }
      return Object.assign(result, { [key]: obj[key] });
    }, {}),
  );
  return Object.assign({}, ...filtered);
}

/**
 * Run a function after the next browser repaint.
 *
 * requestAnimationFrame runs in the same frame before the browser paints and
 * any code called there will run right away, unless it's a task scheduling
 * call like another rAF which will then be queued to the next frame.
 *
 * @param {Function} cb - Callback to run. Is passed a DOMHighResTimeStamp
 *   indicating the point in time when the callback starts to execute.
 */
export function afterNextPaint(cb) {
  requestAnimationFrame(() => {
    requestAnimationFrame((timestamp) => {
      cb(timestamp);
    });
  });
}

/**
 * Get a promise that's resolved BEFORE the next browser repaint.
 *
 * @returns {Promise.<number>} Resolved with a DOMHighResTimeStamp indicating
 *   the point in time when it happens.
 */
export function nextFrame() {
  return new Promise(requestAnimationFrame);
}

/**
 * Get a promise that's resolved AFTER the next browser repaint.
 *
 * @returns {Promise.<number>} Resolved with a DOMHighResTimeStamp indicating
 *   the point in time when it happens.
 */
export function nextPaint() {
  return new Promise(afterNextPaint);
}

/**
 * Get a promise that's resolved after after the specified time.
 *
 * @param {number} duration - How long to wait, in miliseconds.
 * @param {*} [resolveWith] - Value to resolve with, defaults to undefined.
 * @returns {Promise.<*>}
 */
export function sleep(duration, resolveWith) {
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve(resolveWith);
    }, duration),
  );
}

/**
 * Ensure a value is an array.
 *
 * @param {*} val - Value to convert.
 * @returns {Array.<*>}
 * @example
 *
 * toArray(3);
 * // => [3]
 *
 * toArray([1, 2, 3]);
 * // => [1, 2, 3]
 */
export function toArray(val) {
  return Array.isArray(val) ? val : [val];
}

/**
 * Create an array of numbers.
 *
 * @param {number} from - The start of the range.
 * @param {number} to - The end of the range.
 * @param {number} [step] - Value to increment or decrement by, defaults to 1.
 * @returns {Array.<number>}
 * @example
 *
 * range(1, 3);
 * // => [1, 2, 3]
 *
 * range(0, 10, 2);
 * // => [0, 2, 4, 6, 8, 10]
 *
 * range(-1, -3, -1);
 * // => [-1, -2, -3]
 */
export function range(from, to, step = 1) {
  let length = Math.max(Math.ceil((to - from) / (step || 1)), 0);
  const result = new Array(length);

  let i = 0;
  while (length > -1) {
    result[i] = from;
    from += step;
    i += 1;
    length -= 1;
  }

  return result;
}

/* ========================================================================= *\
    Type checks
\* ========================================================================= */

/**
 * Check if a value is an array.
 *
 * A bit pointless given it's just an alias, but it's nice to have the same
 * convention for all type checks.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export const { isArray } = Array;

/**
 * Check if a value is `null`.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isNull(val) {
  return val === null;
}

/**
 * Check if a value is `undefined`.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isUndefined(val) {
  return typeof val === 'undefined';
}

/**
 * Check if a value is `null` or `undefined`.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isNil(val) {
  return val == null;
}

/**
 * Check if a value is a function.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isFunc(val) {
  return typeof val === 'function';
}

/**
 * Check if a value is a number.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isNumber(val) {
  return typeof val === 'number' && !Number.isNaN(val);
}

/**
 * Check if a value is a number or numeric string.
 *
 * @param {*} val - The value to check.
 * @returns {boolean}
 */
export function isNumeric(val) {
  return isNumber(val) || val === String(Number(val));
}

/**
 * Check if a value is a string.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isString(val) {
  return typeof val === 'string';
}

/**
 * Check if a value is a boolean.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isBool(val) {
  return val === true || val === false;
}

/**
 * Check if a value is truthy.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isTruthy(val) {
  return Boolean(val);
}

/**
 * Check if a value is falsy.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isFalsy(val) {
  return !val;
}

/**
 * Check if a value is any non-array object.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isObject(val) {
  return Boolean(val && typeof val === 'object' && !isArray(val));
}

/**
 * Check if a value is a plain object (an object literal or created with
 * Object.create(null)).
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isPlainObject(val) {
  return Boolean(
    val &&
      typeof val === 'object' &&
      Object.prototype.toString.call(val) === '[object Object]' &&
      [Object.prototype, null].includes(Object.getPrototypeOf(val)),
  );
}

/**
 * Create a function that does a falsy check against the return value of the
 * passed function when called.
 *
 * @param {Function} func - Function to 'reverse'.
 * @returns {Function}
 * @example
 *
 * // Only keep booleans.
 * someArr.filter(isBool);
 *
 * // Only keep non-booleans.
 * someArr.filter(not(isBool));
 */
export function not(func) {
  return (...args) => !func(...args);
}

/* ========================================================================= *\
    Immutable array modification
\* ========================================================================= */

/**
 * Shallow copy an array.
 *
 * @param {Array.<*>} arr - The array.
 * @returns {Array.<*>} The copy.
 */
export function copyArray(arr) {
  return [...arr];
}

/**
 * Shallow copy an array and add an item to the end of it.
 *
 * @see Array.prototype.push
 * @param {Array.<*>} arr - The array.
 * @param {*} newEntry - The item to add.
 * @returns {Array.<*>} The new array.
 */
export function immutablePush(arr, newEntry) {
  return [...arr, newEntry];
}

/**
 * Shallow copy an array and add an item to the beginning of it.
 *
 * @see Array.prototype.unshift
 * @param {Array.<*>} arr - The array.
 * @param {*} newEntry - The item to add.
 * @returns {Array.<*>} The new array.
 */
export function immutableUnshift(arr, newEntry) {
  return [newEntry, ...arr];
}

/**
 * Shallow copy an array and sort it.
 *
 * @see Array.prototype.sort
 * @param {Array.<*>} arr - The array.
 * @param {Function} compareFunction - Function that defines the sort order.
 * @returns {Array.<*>} The sorted array.
 */
export function immutableSort(arr, compareFunction) {
  return copyArray(arr).sort(compareFunction);
}

/**
 * Shallow copy an array and reverse it.
 *
 * @see Array.prototype.reverse
 * @param {Array.<*>} arr - The array.
 * @returns {Array.<*>} The reversed array.
 */
export function immutableReverse(arr) {
  return copyArray(arr).reverse();
}

/**
 * Shallow copy an array and remove or replace existing items from and/or add
 * new items to it.
 *
 * @see Array.prototype.splice
 * @param {Array.<*>} arr - The array.
 * @param {number} [start] - The index at which to start changing the array.
 * @param {number} [deleteCount] - The number of items to remove, beginning
 *   from `start`.
 * @param {...*} [items] - Elements to add, beginning from `start`.
 * @returns {Array.<*>} The new array.
 */
export function immutableSplice(arr, start, deleteCount = 0, ...items) {
  // Match native
  if (start === undefined) {
    return arr;
  }
  if (!isNumeric(start)) {
    throw new TypeError('Start index should be a number');
  }
  // NaN > 0 evaluates to false, so a non-numeric deleteCount like `null`
  // will work.
  const clearToEnd = !Number(deleteCount) > 0 && !items.length;
  return [
    ...arr.slice(0, Number(start)),
    ...items,
    ...arr.slice(start + (clearToEnd ? arr.length : deleteCount)),
  ];
}

/**
 * Shallow copy an array and delete an item from it.
 *
 * @param {Array.<*>} arr - The array.
 * @param {number} index - Which index to delete.
 * @returns {Array.<*>} The new array.
 */
export function immutableDelete(arr, index) {
  return arr.slice(0, index).concat(arr.slice(index + 1));
}
